/**
 * Peer — combines an EventLog with a Transport.  Implements the gossip
 * protocol that replicates events across all connected peers:
 *
 *   - On local `append(kind, payload)`, broadcast a `share` message
 *     containing the new event to all currently-connected peers.
 *
 *   - On receiving a peer-connect event from the transport, send a
 *     `sync-request` to that peer with this peer's current vector clock.
 *     The remote responds with everything we don't yet have.
 *
 *   - On receiving a `sync-request`, respond with `events.since(clock)`.
 *
 *   - On receiving a `share` or `sync-response`, apply the events to the
 *     local log.  EventLog.apply is idempotent so re-delivery is safe.
 *
 * The protocol is flood-replication: simple, correct, not bandwidth-optimal
 * for large sessions.  Optimization (gossip suppression, snapshot transfer)
 * is a follow-up if real sessions get big.
 */

import { EventLog, type QuireEvent, type PeerId, type VectorClock } from './event-log';
import { materialize, type SessionState } from './state';
import type { Transport, Unsubscribe } from './transport';

interface ShareMessage {
  kind: 'share';
  event: QuireEvent;
}

interface SyncRequestMessage {
  kind: 'sync-request';
  clock: VectorClock;
}

interface SyncResponseMessage {
  kind: 'sync-response';
  events: QuireEvent[];
}

type ProtocolMessage = ShareMessage | SyncRequestMessage | SyncResponseMessage;

function isProtocolMessage(value: unknown): value is ProtocolMessage {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  switch (v.kind) {
    case 'share':
      return !!v.event && typeof v.event === 'object';
    case 'sync-request':
      return !!v.clock && typeof v.clock === 'object' && !Array.isArray(v.clock);
    case 'sync-response':
      return Array.isArray(v.events);
    default:
      return false;
  }
}

export type StateChangeHandler = (state: SessionState) => void;

/**
 * NEW-ADV-2 (2026-05-29 save-restore program, independent
 * adversarial review): rebroadcast firewall filter.  Returns the
 * (possibly scrubbed) event to forward, or `null` to drop entirely.
 * See `persistence.ts:defaultRebroadcastFilter` for the production
 * wiring and `defaultRebroadcastFilter` doc for the rationale.
 *
 * Injected (rather than imported) so the `core/` layer doesn't
 * depend on `persistence.ts`.  Default in this file is identity
 * (preserves prior behavior for tests that build a bare Peer
 * without a wired-up controller — the default is unsafe in
 * production but matches the pre-NEW-ADV-2 state so existing tests
 * that don't care about DM-only events keep passing).  The
 * session-controller / quire-app wiring MUST pass a real filter.
 */
export type RebroadcastFilter = (event: QuireEvent) => QuireEvent | null;

/**
 * OP-039 (2026-05-29 save-restore program, mock campaign 01 finding):
 * sync-response firewall filter.  Same shape as `RebroadcastFilter`
 * but applied at a different seam (sync-request → sync-response,
 * the catch-up channel for a joining peer).  Production wiring uses
 * a narrower default that drops only whole-kind DM-only events so
 * partial-DM-only events (e.g. `pc-edit knowsTheyCanCast` for the
 * joining player's OWN PC) still reach the receiver, who runs the
 * full viewer-context firewall via `filterForViewer` at render.
 *
 * Injected separately so the two surfaces can evolve independently;
 * default in this file is identity for the same backward-compat
 * reason as `RebroadcastFilter`.  Session-controller wires the real
 * defaults via `defaultSyncResponseFilter`.
 */
export type SyncResponseFilter = (event: QuireEvent) => QuireEvent | null;

const IDENTITY_REBROADCAST_FILTER: RebroadcastFilter = (event) => event;
const IDENTITY_SYNC_RESPONSE_FILTER: SyncResponseFilter = (event) => event;

export class Peer {
  private readonly log: EventLog;
  private readonly stateListeners = new Set<StateChangeHandler>();
  private readonly unsubscribes: Unsubscribe[] = [];
  private readonly rebroadcastFilter: RebroadcastFilter;
  private readonly syncResponseFilter: SyncResponseFilter;
  /**
   * #412 (E-PERF): memoize the materialize fold, keyed on the log
   * revision.  `state()` is called redundantly within a single logical
   * update — `view()` reads it, coordinator-checks read it, and a local
   * `append` fires `notifyStateChange` (→ a notify) AND the controller
   * method then notifies again, so a single append materialized the
   * whole log twice.  The memo collapses all same-revision reads to one
   * fold.  Firewall-NEUTRAL: this caches the UNFILTERED state; the
   * per-viewer `filterForViewer` projection runs fresh on every `view()`.
   */
  private _stateMemo: { revision: number; state: SessionState } | null = null;

  constructor(
    public readonly peerId: PeerId,
    private readonly transport: Transport,
    options: {
      rebroadcastFilter?: RebroadcastFilter;
      syncResponseFilter?: SyncResponseFilter;
    } = {}
  ) {
    this.log = new EventLog(peerId);
    this.rebroadcastFilter =
      options.rebroadcastFilter ?? IDENTITY_REBROADCAST_FILTER;
    this.syncResponseFilter =
      options.syncResponseFilter ?? IDENTITY_SYNC_RESPONSE_FILTER;

    this.unsubscribes.push(
      transport.onMessage((from, payload) => this.handleMessage(from, payload))
    );
    this.unsubscribes.push(
      transport.onPeerConnect((other) => this.requestSync(other))
    );
    // onPeerDisconnect: nothing to do; events stay in log for next-sync catchup.

    // Catch up on peers that were already connected when our handlers
    // attached.  The in-memory transport's `register` runs synchronously in
    // its constructor, before this Peer constructor body runs, so:
    //
    //   - Connect notifications from other transports targeting us fire
    //     into a void (our onPeerConnect handler isn't attached yet).
    //   - Sync-requests sent by other peers' onPeerConnect handlers in
    //     response to seeing us join *also* land in a void (our onMessage
    //     handler isn't attached yet).
    //
    // The pull below is what saves convergence: we ask every already-
    // connected peer for their events, they respond with sync-response, we
    // catch up.  If anyone ever adds a "push initial state on connect"
    // optimization to this protocol, that push must NOT assume the new
    // peer's onMessage is wired — it isn't, until after this loop runs.
    // Convergence must stay pull-driven from the new peer's side.
    for (const other of transport.connectedPeers()) {
      this.requestSync(other);
    }
  }

  /** Append a local event and broadcast it. */
  append(kind: string, payload: unknown): QuireEvent {
    const event = this.log.append(kind, payload);
    this.transport.send('broadcast', { kind: 'share', event } satisfies ShareMessage);
    this.notifyStateChange();
    return event;
  }

  /**
   * Take over as session coordinator.  Unconditional: succeeds even
   * if another peer is currently coordinator (this is the supported
   * sick-DM-handoff workflow).  Synthesizes an audit chat entry
   * visible to all peers; callers are expected to gate this behind
   * a deliberate UI action with a named-coordinator confirmation
   * dialog (see UI in Phase 2b).
   */
  reclaimCoordinator(): QuireEvent {
    const fromPeerId = this.state().coordinator;
    return this.append('coordinator-reclaim', { fromPeerId });
  }

  /** Current materialized state (memoized per log revision). */
  state(): SessionState {
    const revision = this.log.revision();
    const memo = this._stateMemo;
    if (memo !== null && memo.revision === revision) return memo.state;
    const state = materialize(this.log.events());
    this._stateMemo = { revision, state };
    return state;
  }

  /** Snapshot of the current vector clock. */
  clock(): VectorClock {
    return this.log.snapshot();
  }

  /** Subscribe to state changes.  Called after every local or remote event applied. */
  onStateChange(handler: StateChangeHandler): Unsubscribe {
    this.stateListeners.add(handler);
    return () => this.stateListeners.delete(handler);
  }

  /** For inspection / testing — direct access to the event list. */
  events(): readonly QuireEvent[] {
    return this.log.events();
  }

  /**
   * Apply an externally-provided event to the local log.  Used by
   * load/restore paths.
   *
   * M3 (2026-05-29 save-restore program, Architect finding #1 +
   * peer.restore-rebroadcast.test.ts reproduction): by default we
   * propagate newly-applied events to connected peers via the
   * sync-response gossip path (hub-forwarding).  Without this,
   * a peer that loads N events from save AFTER joining an existing
   * session never sends those events to peers who already
   * sync-requested before the load happened.  The pull-only model
   * left a permanent gap.
   *
   * We use `forwardShareToOthers` rather than the `share` envelope
   * because the event author may be a PRIOR peer from the saved
   * session (e.g. "bob's event-log was reconstructed from alice's
   * autosave"), and the R2.1 impersonation defense rejects `share`
   * messages whose event.peerId differs from the transport sender.
   * `sync-response` is exempt by design — gossip-forwarding
   * inherently re-ships events authored by others.
   *
   * Callers that explicitly do NOT want propagation (e.g. internal
   * dedup tests) can pass `{ propagate: false }`.  The
   * `regenerateCode` path also uses no-propagate because it
   * leaves + re-joins the network and the new connections handle
   * catch-up via the normal sync-request path.
   *
   * Returns whether the event was newly applied (false if duplicate
   * or invalid).
   */
  applyEvent(
    event: QuireEvent,
    options: { propagate?: boolean } = {}
  ): boolean {
    const propagate = options.propagate !== false;
    const applied = this.log.apply(event);
    if (!applied) return false;
    if (propagate) {
      // Treat ourselves as the "originalSender" — forwardShareToOthers
      // ships to every connected peer except us, which is exactly the
      // intent here.  Each recipient applies via sync-response (also
      // exempt from re-forwarding to avoid loops), then their own
      // notifyStateChange fires.  EventLog dedup at the recipient
      // makes the operation idempotent if another path delivered
      // the same event first.
      this.forwardShareToOthers(this.peerId, event);
    }
    this.notifyStateChange();
    return true;
  }

  close(): void {
    for (const unsub of this.unsubscribes) unsub();
    this.unsubscribes.length = 0;
    this.stateListeners.clear();
    this.transport.close();
  }

  private requestSync(otherPeer: PeerId): void {
    this.transport.send(otherPeer, {
      kind: 'sync-request',
      clock: this.log.snapshot()
    } satisfies SyncRequestMessage);
  }

  /**
   * Forward an event we just received via 'share' to every other
   * connected peer, packaged as a sync-response.  Hub-forwarding
   * — required in hub topologies (PeerJS) where the originating
   * guest's broadcast only reached us.  See peer.hub-forward.test.ts
   * for the F-MAJOR finding that prompted this.
   *
   * NEW-ADV-2 (2026-05-29 save-restore program): every forwarded
   * event passes through the rebroadcast firewall filter first.
   * DM-only kinds are dropped (a peer holding restored DM material
   * MUST NOT relay it).  Player-visible-with-DM-subfields events
   * are field-scrubbed via the same `PER_KIND_SCRUBBERS` registry
   * the save-side filter uses.  This is defense in depth: today's
   * `share` authenticity check + save-side projection mean such
   * events shouldn't be in our log unless we loaded them from a
   * coord save while in a non-coord role; this filter is the
   * second line of defense at the gossip boundary.
   *
   * Why filter on EVERY hub-forward (not just applyEvent-originated):
   * the same DM-only-event-in-our-log condition can be reached via
   * a peer who joined mid-handoff with a buggy or hostile save.
   * We don't trust our own log to be free of DM-only events when
   * deciding what to send to others.  The author who legitimately
   * holds DM-only events still broadcasts them via `share`
   * (`transport.send('broadcast', ...)`) at original-author time,
   * which reaches every peer directly — hub-forward is for
   * relaying events authored by other peers, where this filter
   * has zero legitimate cost.
   */
  private forwardShareToOthers(originalSender: PeerId, event: QuireEvent): void {
    const others = this.transport
      .connectedPeers()
      .filter((p) => p !== originalSender);
    if (others.length === 0) return;
    const filtered = this.rebroadcastFilter(event);
    if (filtered === null) return;
    const msg: SyncResponseMessage = {
      kind: 'sync-response',
      events: [filtered]
    };
    for (const other of others) {
      this.transport.send(other, msg);
    }
  }

  private handleMessage(from: PeerId, payload: unknown): void {
    if (!isProtocolMessage(payload)) return;
    switch (payload.kind) {
      case 'share': {
        // Impersonation defense: a `share` is a NEW event from its
        // author, so the transport sender MUST equal the event's
        // claimed peerId.  Without this, mallory could broadcast a
        // 'coordinator-claim' or 'scene-reveal' event with
        // peerId='alice' and shadow-author actions attributed to
        // alice.  This is the cheapest authenticity check we can do
        // without cryptographic signatures.
        //
        // sync-response (below) is intentionally NOT subject to this
        // check — gossip forwarding requires peers to ship events
        // authored by others.  That tradeoff is documented; closing
        // it would require per-event signatures.
        if (payload.event && payload.event.peerId !== from) {
          // Silently drop — no need to alert the attacker by
          // responding.  Future: log this for monitoring.
          break;
        }
        if (this.log.apply(payload.event)) {
          this.notifyStateChange();
          // Hub-forwarding (F-MAJOR fix discovered in Phase 3
          // simulation): in a hub topology (PeerJS guests connect
          // only to the host), the original sender's broadcast
          // only reaches the host.  Other guests never see the
          // event unless someone forwards it.  We forward by
          // emitting a sync-response containing this single
          // event to every connected peer EXCEPT the sender.
          // sync-response is exempt from the R2.1 cross-check
          // because gossip-forwarding inherently re-ships events
          // authored by others.  Recipients apply via the
          // sync-response branch below; they don't re-forward,
          // so the protocol terminates in one hop.
          this.forwardShareToOthers(from, payload.event);
        }
        break;
      }
      case 'sync-request': {
        const events = this.log.since(payload.clock);
        // OP-039 (2026-05-29 save-restore program, mock campaign 01
        // finding): the sync-response path is a sister firewall
        // surface to `forwardShareToOthers`.  A peer holding
        // DM-only events (loaded from a coord save while in a
        // non-coord role, or temporarily caching them while
        // coordinator) MUST NOT relay them to a joining peer.
        // Without this filter the joining peer's raw event log
        // carries DM scratch-notes / pins / etc. — invisible at
        // render time (filterForViewer) and at save time
        // (serializeSessionForViewer) but devtools-visible and a
        // hole in the firewall SSOT.
        //
        // Narrower than `rebroadcastFilter` by design: production
        // wires `defaultSyncResponseFilter`, which drops whole-
        // kind DM-only events (PLAYER_SCOPE_STRIP_KINDS) but does
        // NOT run per-field scrubbers.  Sync-response is a fresh
        // peer's ONLY catch-up channel for events that happened
        // before they connected; partial-DM-only events (e.g.
        // `pc-edit knowsTheyCanCast` for the joining player's OWN
        // PC) must survive so filterForViewer on the receiver can
        // do the per-viewer projection with full context.  See
        // `persistence.ts:defaultSyncResponseFilter` for the
        // architectural rationale.
        const filtered: QuireEvent[] = [];
        for (const event of events) {
          const result = this.syncResponseFilter(event);
          if (result !== null) filtered.push(result);
        }
        if (filtered.length > 0) {
          this.transport.send(from, {
            kind: 'sync-response',
            events: filtered
          } satisfies SyncResponseMessage);
        }
        break;
      }
      case 'sync-response': {
        let changed = false;
        for (const event of payload.events) {
          if (this.log.apply(event)) {
            changed = true;
            // Hub-forwarding (extended F-MAJOR fix): forward
            // newly-applied events from a sync-response to OTHER
            // peers too.  Required because peer-join broadcasts
            // race the PeerJS data-channel handshake: when a new
            // guest's runJoin appends peer-join immediately after
            // attachPeer, the data channel to the host may not be
            // open yet, so the original 'share' is dropped (the
            // connections map is empty).  The new guest then
            // catches up via sync-response from the host, but
            // without re-forwarding the host has no way to push
            // the new guest's peer-join to other guests — so
            // every guest's shared.peers stays incomplete.
            // Dedup at the EventLog id-level prevents loops.
            this.forwardShareToOthers(from, event);
          }
        }
        if (changed) this.notifyStateChange();
        break;
      }
    }
  }

  private notifyStateChange(): void {
    const state = this.state();
    for (const h of this.stateListeners) h(state);
  }
}
